import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Sparkles, ArrowRight, ChefHat, Search, Globe, Clock, ShieldCheck,
    Wand2, Zap, ImageIcon, CheckCircle, Calendar, FileQuestion,
} from "lucide-react";
import { FoxMark, Wordmark } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import PublicFooter from "@/components/PublicFooter";
import PublicHeader from "@/components/public/PublicHeader";
import ExploreSection from "@/components/public/ExploreSection";
import useDocumentMeta, { SITE_URL } from "@/lib/useDocumentMeta";
import { faqLd } from "@/components/public/Blocks";

const FAQ = [
    { q: "How is RecipeFox AI different from ChatGPT for recipes?", a: "ChatGPT gives you raw text. RecipeFox produces a complete WordPress-ready article: humanized intro, ingredients, step-by-step instructions, 3 photoreal images, FAQs, nutrition, Schema.org markup, SEO meta, taxonomies, and pushes it all to your live site." },
    { q: "Will Google penalize AI-generated content?", a: "Google penalizes low-quality content, not AI itself. RecipeFox follows Google's helpful-content guidelines with proper schema, unique content per keyword, and human-readable structure. Used responsibly, articles rank like hand-written ones." },
    { q: "Which WordPress plugins are supported?", a: "WP Recipe Maker and WP Delicious. Full end-to-end coverage: ingredients with quantities and units, instructions, nutrition (15+ micronutrients), taxonomies, featured image, FAQs, and Schema." },
    { q: "What does it cost?", a: "RecipeFox itself is free. You bring your own API keys for Anthropic Claude and kie.ai. Typical fully-generated recipe costs ~$0.04 (text + 3 photoreal images). Cost preview shows the estimate before you commit." },
    { q: "How fast can I publish my first recipe?", a: "Under 5 minutes from signup. Three steps: add API keys, connect WordPress (Application Password), generate. Bulk mode lets you queue 50+ recipes across a schedule with one click." },
];

const CAROUSEL_IMAGES = [
    { src: "/landing-hero.jpg", caption: "Hero shot — featured image" },
    { src: "/landing-process.jpg", caption: "Process shot — mid-recipe" },
    { src: "/landing-serving.jpg", caption: "Serving shot — lifestyle" },
];

const fade = {
    hidden: { opacity: 0, y: 20 },
    show: (i = 0) => ({
        opacity: 1, y: 0,
        transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }
    }),
};

const LANDING_LD = {
    "@context": "https://schema.org",
    "@graph": [
        { "@type": "SoftwareApplication", "@id": `${SITE_URL}/#app`, name: "RecipeFox AI", applicationCategory: "BusinessApplication",
          operatingSystem: "Web", url: SITE_URL, publisher: { "@id": `${SITE_URL}/#org` },
          description: "AI recipe generator that writes complete recipe articles and publishes them to WordPress with WP Recipe Maker or WP Delicious.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } },
        faqLd(FAQ, "/"),
    ],
};

export default function Landing() {
    const { user } = useAuth();
    useDocumentMeta({
        title: "RecipeFox AI: AI Recipe Generator That Publishes to WordPress",
        description: "Turn a keyword into a complete recipe post on WordPress: filled WP Recipe Maker or WP Delicious card, three photoreal images, checked times and ingredients, schema and Yoast meta.",
        path: "/", image: "/landing-hero.jpg", jsonLd: LANDING_LD,
    });

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="landing-page">
            <PublicHeader />

            {/* ============== HERO ============== */}
            <section className="relative overflow-hidden border-b border-line">
                {/* animated background gradient */}
                <motion.div
                    aria-hidden
                    className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none"
                    style={{
                        background: "radial-gradient(circle, rgba(31,61,186,0.15) 0%, transparent 60%)",
                    }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                    aria-hidden
                    className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full pointer-events-none"
                    style={{
                        background: "radial-gradient(circle, rgba(220,80,40,0.08) 0%, transparent 60%)",
                    }}
                    animate={{ rotate: -360 }}
                    transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
                />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <motion.div initial="hidden" animate="show" variants={fade} className="overline mb-6 flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5 text-klein" />
                            AI recipe generator for WordPress
                        </motion.div>
                        <motion.h1
                            initial="hidden" animate="show" variants={fade} custom={1}
                            className="heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                            data-testid="landing-h1"
                        >
                            Keyword in.{" "}<br />
                            <span className="text-klein">Recipe article out.</span>{" "}<br />
                            <span className="text-ink2">Published on WordPress.</span>
                        </motion.h1>
                        <motion.p
                            initial="hidden" animate="show" variants={fade} custom={2}
                            className="mt-8 text-lg text-ink2 max-w-xl leading-relaxed"
                        >
                            RecipeFox AI writes a complete, humanized, SEO-optimized recipe article and pushes it to
                            your WordPress with WP Recipe Maker or WP Delicious. Featured images, recipe schema,
                            FAQ, taxonomies, Yoast/RankMath meta. End to end. Hands free.
                        </motion.p>
                        <motion.div
                            initial="hidden" animate="show" variants={fade} custom={3}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            <Link to={user ? "/generate" : "/register"}
                                className="px-7 py-3.5 bg-klein text-white hover:bg-kleinDark inline-flex items-center gap-2 font-medium"
                                data-testid="landing-hero-cta">
                                {user ? "Open app" : "Start free"} <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/get-started"
                                className="px-7 py-3.5 border border-line bg-white hover:bg-paperWarm inline-flex items-center gap-2"
                                data-testid="landing-hero-howto">
                                How it works
                            </Link>
                        </motion.div>
                        <motion.div
                            initial="hidden" animate="show" variants={fade} custom={4}
                            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink2"
                        >
                            <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-klein" /> Free to start</span>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-klein" /> Your own API keys</span>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-klein" /> ~$0.04 per recipe</span>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-klein" /> GDPR ready</span>
                        </motion.div>
                    </div>

                    {/* Animated mosaic */}
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5]">
                            {CAROUSEL_IMAGES.map((img, i) => (
                                <motion.div
                                    key={img.src}
                                    initial={{ opacity: 0, scale: 0.9, rotate: (i - 1) * 4 }}
                                    animate={{ opacity: 1, scale: 1, rotate: (i - 1) * 4 }}
                                    transition={{ delay: 0.3 + i * 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                    whileHover={{ scale: 1.04, rotate: 0, zIndex: 5 }}
                                    className="absolute border border-line bg-white shadow-lg"
                                    style={{
                                        top: `${i * 15}%`,
                                        left: `${i * 10}%`,
                                        right: `${(2 - i) * 10}%`,
                                        height: "60%",
                                    }}
                                >
                                    <img src={img.src} alt={img.caption}
                                        className="w-full h-full object-cover"
                                        loading={i === 0 ? "eager" : "lazy"}
                                    />
                                    <div className="absolute bottom-2 left-2 right-2 text-[10px] font-mono text-white bg-black/60 px-2 py-1">
                                        {img.caption}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== LIVE STAT STRIP ============== */}
            <section className="bg-ink text-paper">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { v: "5 min", l: "From signup to first recipe" },
                        { v: "~$0.04", l: "Per fully generated recipe" },
                        { v: "74", l: "WP Delicious fields populated" },
                        { v: "2", l: "Plugins supported (WPRM + WPD)" },
                    ].map((s, i) => (
                        <motion.div
                            key={s.l}
                            initial="hidden" whileInView="show" viewport={{ once: true }}
                            variants={fade} custom={i}
                        >
                            <div className="text-3xl sm:text-4xl heading">{s.v}</div>
                            <div className="text-paper/60 text-xs mt-1 font-mono uppercase">{s.l}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ============== HOW IT WORKS ============== */}
            <section className="border-b border-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="grid lg:grid-cols-12 gap-10 mb-14">
                        <div className="lg:col-span-5">
                            <div className="overline mb-4">How it works</div>
                            <h2 className="heading text-4xl sm:text-5xl leading-[1.05]">
                                Built for food bloggers who value their time.
                            </h2>
                        </div>
                        <p className="lg:col-span-7 text-ink2 text-lg leading-relaxed self-end">
                            Type a keyword. We generate a humanized recipe article with photoreal images,
                            structured data, and SEO meta — then publish it on your WordPress. No prompt
                            engineering, no copy-paste, no plugin headaches.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Search, t: "1 · Keyword", b: "Paste a recipe idea or import 50 from CSV/text." },
                            { icon: Wand2, t: "2 · Generate", b: "Claude writes a humanized article. kie.ai paints 3 photoreal images." },
                            { icon: ShieldCheck, t: "3 · Review", b: "Preview with full schema, regenerate any image you don't love." },
                            { icon: Calendar, t: "4 · Schedule", b: "Auto-publish across a date range with randomized natural cadence." },
                        ].map((s, i) => (
                            <motion.div
                                key={s.t}
                                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }}
                                variants={fade} custom={i}
                                className="bg-white border border-line p-6 hover:border-klein transition-colors"
                            >
                                <div className="w-10 h-10 bg-klein/10 flex items-center justify-center mb-4">
                                    <s.icon className="w-5 h-5 text-klein" />
                                </div>
                                <h3 className="font-semibold mb-2">{s.t}</h3>
                                <p className="text-sm text-ink2 leading-relaxed">{s.b}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============== VS / FEATURE BLOCK ============== */}
            <section className="bg-paperWarm border-b border-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="overline mb-4">RecipeFox vs. raw LLMs</div>
                    <h2 className="heading text-4xl sm:text-5xl leading-[1.05] mb-12 max-w-2xl">
                        ChatGPT gives you text.<br />
                        <span className="text-klein">We give you a published article.</span>
                    </h2>
                    <div className="grid md:grid-cols-2 gap-px bg-line">
                        <div className="bg-white p-8">
                            <div className="overline text-ink2 mb-3">Raw LLM</div>
                            <ul className="space-y-3 text-sm text-ink2">
                                {[
                                    "Long-winded fluff, AI clichés like 'in today's fast-paced world'",
                                    "Manual cleanup to fit recipe plugin",
                                    "No image generation",
                                    "No SEO meta, no schema markup",
                                    "Copy-paste each step to WordPress",
                                    "No bulk scheduling",
                                ].map(line => <li key={line} className="flex items-start gap-2">— {line}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white p-8 border-t md:border-t-0 md:border-l-2 border-klein">
                            <div className="overline text-klein mb-3 flex items-center gap-2">
                                <Zap className="w-3.5 h-3.5" /> RecipeFox AI
                            </div>
                            <ul className="space-y-3 text-sm">
                                {[
                                    "Humanized prose with banned AI phrases stripped",
                                    "Direct push to WPRM or WP Delicious fields",
                                    "3 photoreal images (hero + process + serving)",
                                    "Yoast/RankMath/AIOSEO + Recipe + FAQ schema",
                                    "One-click publish — bridge plugin handles meta",
                                    "Bulk 50 recipes, randomized over your schedule",
                                ].map(line => (
                                    <li key={line} className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-klein mt-0.5 flex-shrink-0" />
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== TECH STACK ============== */}
            <section className="bg-ink text-paper border-b border-ink">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                    <h2 className="heading text-3xl sm:text-4xl mb-10 max-w-2xl">
                        Production-grade, not a wrapper toy.
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6 font-mono text-sm">
                        {[
                            ["LLM", "Claude Sonnet 5.5"],
                            ["Images", "kie.ai Nano Banana 2"],
                            ["Recipe plugins", "WPRM + WP Delicious"],
                            ["SEO meta", "Yoast / RankMath / AIOSEO"],
                            ["Structured data", "Recipe + FAQ JSON-LD"],
                            ["Auth", "JWT + WP Application Password"],
                            ["Cost tracking", "Per recipe + 30-day average"],
                            ["GDPR", "Export + delete in one click"],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <div className="text-paper/50 text-xs uppercase tracking-wider">{k}</div>
                                <div className="mt-1">{v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <ExploreSection />

            {/* ============== FAQ ============== */}
            <section className="border-b border-line">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
                    <div className="flex items-center gap-2 overline mb-4">
                        <FileQuestion className="w-4 h-4 text-klein" />
                        Frequently asked
                    </div>
                    <h2 className="heading text-4xl sm:text-5xl mb-10">Honest answers, no marketing fluff.</h2>
                    <dl className="divide-y divide-line">
                        {FAQ.map((f, i) => (
                            <motion.div
                                key={f.q}
                                initial="hidden" whileInView="show" viewport={{ once: true }}
                                variants={fade} custom={i}
                                className="py-6"
                                data-testid={`landing-faq-${i + 1}`}
                            >
                                <dt className="font-semibold text-lg mb-2">{f.q}</dt>
                                <dd className="text-ink2 leading-relaxed">{f.a}</dd>
                            </motion.div>
                        ))}
                    </dl>
                </div>
            </section>

            {/* ============== CTA ============== */}
            <section className="bg-klein text-white">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
                    <motion.h2
                        initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                        className="heading text-4xl sm:text-5xl leading-[1.05]"
                    >
                        Ready to ship recipes{" "}<br />while you sleep?
                    </motion.h2>
                    <p className="text-white/70 mt-5 leading-relaxed">
                        Free to start. Add your Anthropic and kie.ai keys in Settings. Connect WordPress in 60 seconds.
                        Generate your first article in under five minutes.
                    </p>
                    <Link to="/register"
                        className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-white text-klein hover:bg-paperWarm font-medium"
                        data-testid="landing-cta-bottom">
                        Create free account <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
